import type { Graphics } from 'pixi.js';

/*
 * Wordless hangar glyphs (docs/09 wave 4 "4-year-old rule"): the SAME bag,
 * padlock, sparkle, swap and arrow shapes on the world crate label, the HUD
 * strip, the pickup toast and the shop. Graphics only — centred on (x, y),
 * `s` = half-size in px.
 */

/**
 * Weapon bag: a tied sack (round belly, cinched neck, flared tuft) — it
 * must never read as the padlock (arched shackle on a box).
 */
export function bagGlyph(g: Graphics, x: number, y: number, s: number, color: number): Graphics {
  g.ellipse(x, y + s * 0.28, s, s * 0.78).fill(color);
  g.poly([
    x - s * 0.42, y - s * 0.4, x + s * 0.42, y - s * 0.4,
    x + s * 0.2, y - s * 0.72, x - s * 0.2, y - s * 0.72,
  ]).fill(color);
  g.poly([
    x - s * 0.2, y - s * 0.7, x - s * 0.62, y - s * 1.08,
    x + s * 0.62, y - s * 1.08, x + s * 0.2, y - s * 0.7,
  ]).fill(color);
  // Tie band + a patch on the belly
  g.rect(x - s * 0.34, y - s * 0.66, s * 0.68, s * 0.16).fill({ color: 0x0f0c1a, alpha: 0.55 });
  g.circle(x + s * 0.32, y + s * 0.38, s * 0.16).fill({ color: 0x0f0c1a, alpha: 0.35 });
  return g;
}

/** Padlock: shackle + body with a keyhole. */
export function padlockGlyph(g: Graphics, x: number, y: number, s: number, color: number): Graphics {
  // moveTo first: an arc would otherwise join the previous path's last point
  g.moveTo(x - s * 0.55, y - s * 0.3)
    .arc(x, y - s * 0.3, s * 0.55, Math.PI, 0).stroke({ color, width: Math.max(2, s * 0.26), cap: 'round' });
  g.roundRect(x - s * 0.8, y - s * 0.3, s * 1.6, s * 1.2, s * 0.25).fill(color);
  g.circle(x, y + s * 0.2, s * 0.17).fill(0x0f0c1a);
  return g;
}

/** Four-point "new" sparkle. */
export function sparkleGlyph(g: Graphics, x: number, y: number, s: number, color: number): Graphics {
  const k = s * 0.28;
  g.poly([x, y - s, x + k, y - k, x + s, y, x + k, y + k, x, y + s, x - k, y + k, x - s, y, x - k, y - k])
    .fill(color);
  return g;
}

/** ⇄ swap: two opposed arrows. */
export function swapGlyph(g: Graphics, x: number, y: number, s: number, color: number): Graphics {
  const w = Math.max(2, s * 0.24);
  g.moveTo(x - s, y - s * 0.38).lineTo(x + s * 0.7, y - s * 0.38).stroke({ color, width: w, cap: 'round' });
  g.poly([x + s, y - s * 0.38, x + s * 0.45, y - s * 0.78, x + s * 0.45, y + 0.02 * s]).fill(color);
  g.moveTo(x + s, y + s * 0.38).lineTo(x - s * 0.7, y + s * 0.38).stroke({ color, width: w, cap: 'round' });
  g.poly([x - s, y + s * 0.38, x - s * 0.45, y - 0.02 * s, x - s * 0.45, y + s * 0.78]).fill(color);
  return g;
}

/** Solid arrow pointing along (dx, dy) — "goes into". */
export function arrowGlyph(
  g: Graphics, x: number, y: number, s: number, color: number, dx = 1, dy = 0,
): Graphics {
  const px = -dy;
  const py = dx;
  const w = s * 0.32;
  g.poly([
    x - dx * s + px * w, y - dy * s + py * w,
    x + dx * s * 0.15 + px * w, y + dy * s * 0.15 + py * w,
    x + dx * s * 0.15 + px * s * 0.7, y + dy * s * 0.15 + py * s * 0.7,
    x + dx * s, y + dy * s,
    x + dx * s * 0.15 - px * s * 0.7, y + dy * s * 0.15 - py * s * 0.7,
    x + dx * s * 0.15 - px * w, y + dy * s * 0.15 - py * w,
    x - dx * s - px * w, y - dy * s - py * w,
  ]).fill(color);
  return g;
}

/** ▲ (up = true) / ▼ comparison arrow. */
export function deltaGlyph(g: Graphics, x: number, y: number, s: number, color: number, up: boolean): Graphics {
  const d = up ? -1 : 1;
  g.poly([x - s, y - d * s * 0.6, x + s, y - d * s * 0.6, x, y + d * s * 0.8]).fill(color);
  return g;
}

/** Big "+" (empty socket: put a weapon here). */
export function plusGlyph(g: Graphics, x: number, y: number, s: number, color: number): Graphics {
  const w = Math.max(3, s * 0.3);
  g.roundRect(x - s, y - w / 2, s * 2, w, w / 2).fill(color);
  g.roundRect(x - w / 2, y - s, w, s * 2, w / 2).fill(color);
  return g;
}

/** Red ✕ (throw away). */
export function crossGlyph(g: Graphics, x: number, y: number, s: number, color: number): Graphics {
  const w = Math.max(2, s * 0.32);
  g.moveTo(x - s, y - s).lineTo(x + s, y + s).moveTo(x + s, y - s).lineTo(x - s, y + s)
    .stroke({ color, width: w, cap: 'round' });
  return g;
}

/** ⟳ reroll: two arcs with arrowheads (the reroll bar and the prompt dock). */
export function cycleGlyph(g: Graphics, x: number, y: number, r: number, color: number): Graphics {
  const k = r / 12;
  const w = Math.max(2, 4 * k);
  g.moveTo(x + Math.cos(-2.3) * r, y + Math.sin(-2.3) * r)
    .arc(x, y, r, -2.3, 0.6).stroke({ color, width: w, cap: 'round' });
  g.poly([x + 14 * k, y - k, x + 8 * k, y - 9 * k, x + 4 * k, y + k]).fill(color);
  g.moveTo(x + Math.cos(0.85) * r, y + Math.sin(0.85) * r)
    .arc(x, y, r, 0.85, 3.75).stroke({ color, width: w, cap: 'round' });
  g.poly([x - 14 * k, y + k, x - 8 * k, y + 9 * k, x - 4 * k, y - k]).fill(color);
  return g;
}
